import { observer } from "mobx-react";
import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { deleteWorkbook } from "../../actions/deleteWorkbook";
import { getWorkbooks } from "../../actions/getWorkbooks";
import { lockWorkbook } from "../../actions/lockWorkbook";
import { lang } from "../../lang";
import { MenuItem, Workbook } from "../../interface";
import { store } from "../../store";
import { browserState } from "../../store/browserState";
import { Button } from "../../components/atoms/Button";
import { CenteredContent } from "../../components/atoms/CenteredContent";
import { FlexHorizontal, FlexSpacer } from "../../components/atoms/Flex";
import { Title } from "../../components/atoms/Text";
import { PopupMenu } from "../../components/molecules/PopupMenu";
import { PopupConfirm } from "../../components/molecules/PopupConfirm";
import { Table } from "../../components/molecules/Table";
import { CreateWorkbook } from "../../components/organisms/CreateWorkbook";
import { Template } from "../../components/organisms/Template";
import LockIcon from "@mui/icons-material/Lock";
import LockOutlineIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlineIcon from "@mui/icons-material/LockOpenOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import EditOutlineIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ROUTE_TYPE } from "../../HelperSrc";
import { getDownloadList } from "../../actions/getDownloadList";
import { catchEndpoint } from "../../catchEndpoint";

@observer
class WorkbooksPageBase extends React.Component<RouteComponentProps> {
    public state = {
        addPopup: false,
        deletePopup: null as unknown as Workbook,
        editPopup: null as unknown as Workbook,
        workbookMenu: null as unknown as Workbook,
        x: 0,
        y: 0,
        primaryWBLoading: false,
        secondaryWBLoading: false
    };
    public componentDidMount() {
        if (!store.db) {
            this.props.history.replace(ROUTE_TYPE.CLIENTS);
            return;
        }
        this.loadWorkbook();
    }
    public loadWorkbook() {
        this.setState({
            primaryWBLoading: true,
            secondaryWBLoading: true
        });

        // Get Primary Workbooks
        getWorkbooks().finally(() => {
            this.setState({
                primaryWBLoading: false
            });
        });

        // Get Secondary Workbooks
        getWorkbooks(true).finally(() => {
            this.setState({
                secondaryWBLoading: false
            });
        });
    }
    public openWorkbookMenu = (row: Workbook) => {
        this.setState({
            workbookMenu: row,
            x: browserState.width - browserState.mouseX,
            y: browserState.mouseY
        });
    };
    public selectMenuItem = (menuitem: MenuItem) => {
        const wb = this.state.workbookMenu;
        if (!wb.id) return;
        if (menuitem.key === "files") {
            this.setState({ workbookMenu: false });
            this.props.history.push(`/workbook/${wb.id}/files`);
        } else if (menuitem.key === "download") {
            this.setState({ workbookMenu: false });
            getDownloadList(wb.id.toString()).catch(catchEndpoint);
        } else if (menuitem.key === "downloadonlynew") {
            this.setState({ workbookMenu: false });
            getDownloadList(wb.id.toString(), true).catch(catchEndpoint);
        } else if (menuitem.key === "edit") {
            this.setState({ workbookMenu: false });
            this.setState({
                editPopup: wb
            });
        } else if (menuitem.key === "lock") {
            const sure = confirm(lang.confirmLock);
            if (sure) {
                this.setState({ workbookMenu: false });
                lockWorkbook(wb.id)
                    .then(() => {
                        this.loadWorkbook();
                    })
                    .catch(catchEndpoint);
            }
        } else if (menuitem.key === "unlock") {
            this.setState({ workbookMenu: false });
            lockWorkbook(wb.id, false)
                .then(() => {
                    this.loadWorkbook();
                })
                .catch(catchEndpoint);
        } else if (menuitem.key === "delete") {
            this.setState({ workbookMenu: false });
            this.setState({
                deletePopup: wb
            });
        } else if (menuitem.key === "view") {
            this.setState({ workbookMenu: false });
            this.props.history.push(`/workbook/${wb.id}`);
        }
    };
    public render() {
        const { addPopup, editPopup, workbookMenu } = this.state;
        const { workbooks, secondaryWorkbooks } = store;
        const columnNames = [
            { id: "name", name: lang.name },
            { id: "year", name: lang.year, width: "80px" },
            { id: "last_edit", name: lang.lastModified, width: "180px" },
            { id: "is_locked", name: "", icon: LockIcon, width: "65px" }
        ];
        return (
            <>
                <Template>
                    <CenteredContent>
                        <FlexHorizontal>
                            <Title>{lang.primaryWorkbook}</Title>
                            <FlexSpacer />
                            {store.permission === "CONTROL" && (
                                <div className="container_action">
                                    <Button
                                        id="createworkbookbutton"
                                        data-at_id="open_workbook_popup"
                                        title={lang.create}
                                        onClick={() => {
                                            this.setState({
                                                addPopup: true
                                            });
                                        }}
                                    />
                                </div>
                            )}
                        </FlexHorizontal>
                        <Table
                            id="primary-table"
                            loading={this.state.primaryWBLoading}
                            onClickRow={this.openWorkbookMenu}
                            pageSize={5}
                            columnNames={columnNames}
                            data={workbooks}
                        />

                        <Title>{lang.secondaryWorkbook}</Title>
                        <Table
                            id="secondary-table"
                            loading={this.state.secondaryWBLoading}
                            onClickRow={this.openWorkbookMenu}
                            pageSize={5}
                            columnNames={columnNames}
                            data={secondaryWorkbooks}
                        />
                    </CenteredContent>
                    {workbookMenu && (
                        <PopupMenu
                            id="popup"
                            title={this.state.workbookMenu.name}
                            items={(() => {
                                let itms = [
                                    {
                                        key: "files",
                                        name: lang.manageFiles,
                                        icon: InsertDriveFileOutlinedIcon
                                    },
                                    {
                                        key: "view",
                                        name: lang.viewTables,
                                        icon: TableChartOutlinedIcon
                                    },
                                    {
                                        key: "download",
                                        name: lang.downloadAll,
                                        icon: CloudDownloadOutlinedIcon,
                                        color: "item_download_all"
                                    },
                                    {
                                        key: "downloadonlynew",
                                        name: lang.downloadOnlyNew,
                                        icon: CloudDownloadOutlinedIcon
                                    }
                                ];
                                if (store.permission === "CONTROL") {
                                    if (!workbookMenu.is_primary) {
                                        itms = [
                                            ...itms,
                                            {
                                                key: "edit",
                                                name: lang.editInfo,
                                                icon: EditOutlineIcon
                                            },
                                            {
                                                key: "delete",
                                                name: lang.delete,
                                                icon: DeleteOutlineIcon,
                                                color: "item_delete"
                                            }
                                        ];
                                    } else if (!workbookMenu.is_locked) {
                                        itms = [
                                            ...itms,
                                            {
                                                key: "edit",
                                                name: lang.editInfo,
                                                icon: EditOutlineIcon
                                            },
                                            {
                                                key: "lock",
                                                name: lang.lock,
                                                icon: LockOutlineIcon
                                            },
                                            {
                                                key: "delete",
                                                name: lang.delete,
                                                icon: DeleteOutlineIcon,
                                                color: "item_delete"
                                            }
                                        ];
                                    } else if (store.user.is_staff || store.user.is_superuser) {
                                        itms = [
                                            ...itms,
                                            {
                                                key: "unlock",
                                                name: lang.unlock,
                                                icon: LockOpenOutlineIcon
                                            }
                                        ];
                                    }
                                }
                                return itms;
                            })()}
                            onClick={this.selectMenuItem}
                            onClose={() => {
                                this.setState({ workbookMenu: false });
                            }}
                        />
                    )}
                    {this.state.deletePopup ? (
                        <PopupConfirm
                            onClose={() => {
                                this.setState({ deletePopup: false });
                            }}
                            onClick={() => {
                                this.setState({
                                    deletePopup: false,
                                    primaryWBLoading: true,
                                    secondaryWBLoading: true
                                });
                                deleteWorkbook(this.state.deletePopup.id as number)
                                    .then(() => {
                                        store.doNotice(lang.deleteWorkbook, true);

                                        this.loadWorkbook();
                                    })
                                    .catch(catchEndpoint);
                            }}
                            title={lang.confirmDelete}
                        />
                    ) : null}
                </Template>
                {(addPopup || editPopup) && (
                    <CreateWorkbook
                        edit={editPopup}
                        onClose={(ok?: boolean) => {
                            this.setState({ addPopup: null, editPopup: null });
                            if (ok) {
                                this.loadWorkbook();
                            }
                        }}
                    />
                )}
            </>
        );
    }
}

const WorkbooksPage = withRouter(WorkbooksPageBase);
export { WorkbooksPage };
